import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  CircularProgress,
  Pagination,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  MarkEmailRead as MarkReadIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const PAGE_SIZE = 20;

function IncomingMessages() {
  const [messages, setMessages] = useState([]);
  const [total, setTotal] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const { enqueueSnackbar } = useSnackbar();

  const loadMessages = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/admin/incoming-messages`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { page, limit: PAGE_SIZE }
      });
      setMessages(response.data.messages);
      setTotal(response.data.total);
      setUnreadCount(response.data.unreadCount);
    } catch (err) {
      console.error('Erreur chargement messages reçus:', err);
      enqueueSnackbar('Erreur lors du chargement des messages reçus', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [page, enqueueSnackbar]);

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  const marquerLu = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API_URL}/admin/incoming-messages/${id}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(prev => prev.map(m => (m.id === id ? { ...m, is_read: true } : m)));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Erreur marquage lu:', err);
      enqueueSnackbar('Erreur lors du marquage du message', { variant: 'error' });
    }
  };

  const nomAbonne = (m) => {
    if (!m.subscriber) return null;
    const nom = `${m.subscriber.first_name || ''} ${m.subscriber.last_name || ''}`.trim();
    return nom || null;
  };

  return (
    <Box sx={{ p: 3 }}>
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              💬 Messages reçus des abonnés
              {unreadCount > 0 && (
                <Chip label={`${unreadCount} non lu${unreadCount > 1 ? 's' : ''}`} size="small" color="warning" sx={{ ml: 1 }} />
              )}
            </Typography>
            <Tooltip title="Actualiser">
              <IconButton onClick={loadMessages} size="small" disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : messages.length === 0 ? (
            <Typography color="text.secondary">Aucun message reçu.</Typography>
          ) : (
            <>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Correspondant</TableCell>
                      <TableCell>Téléphone</TableCell>
                      <TableCell>Message</TableCell>
                      <TableCell align="center">Statut</TableCell>
                      <TableCell align="center">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {messages.map((m) => (
                      <TableRow key={m.id} sx={{ bgcolor: m.is_read ? 'inherit' : 'action.hover' }}>
                        <TableCell>{new Date(m.received_at).toLocaleString('fr-FR')}</TableCell>
                        <TableCell>{nomAbonne(m) || <Typography color="text.disabled">Inconnu</Typography>}</TableCell>
                        <TableCell>{m.phone_number || '-'}</TableCell>
                        <TableCell sx={{ maxWidth: 400, whiteSpace: 'pre-wrap' }}>{m.message_body}</TableCell>
                        <TableCell align="center">
                          <Chip
                            label={m.is_read ? 'Lu' : 'Non lu'}
                            size="small"
                            color={m.is_read ? 'success' : 'warning'}
                          />
                        </TableCell>
                        <TableCell align="center">
                          {!m.is_read && (
                            <Tooltip title="Marquer comme lu">
                              <IconButton size="small" onClick={() => marquerLu(m.id)}>
                                <MarkReadIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {total > PAGE_SIZE && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Pagination
                    count={Math.ceil(total / PAGE_SIZE)}
                    page={page}
                    onChange={(_, value) => setPage(value)}
                  />
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}

export default IncomingMessages;
